/** 应用与官网支持的语言（仅中英文） */
export type Lang = 'en' | 'zh';

export const LANG_OPTIONS: { value: Lang; label: string }[] = [
  { value: 'en', label: 'English' },
  { value: 'zh', label: '中文' },
];

type Messages = Partial<Record<Lang, string>>;

const messages: Record<string, Messages> = {
  /** 应用启动 / Supabase 会话校验完成前（与官网中英 localStorage「lang」联动） */
  app_auth_loading: {
    en: 'Loading…',
    zh: '加载中…',
  },
  load_project: { en: 'Load Project', zh: '加载项目' },
  load_project_btn: { en: 'LOAD PROJECT', zh: '加载项目' },
  user_guide: { en: 'User Guide', zh: '使用说明' },
  pricing: { en: 'Pricing', zh: '充值中心' },
  logout: { en: 'Logout', zh: '退出登录' },
  toggle_theme: { en: 'Toggle theme', zh: '切换深浅模式' },
  avatar: { en: 'Avatar', zh: '头像' },
  choose_avatar: { en: 'Choose your avatar', zh: '选择头像' },
  login_required_avatar: { en: 'Please log in to choose an avatar.', zh: '请先登录后再选择头像。' },
  username_placeholder: { en: 'Enter username (A-Z, space, max 16)', zh: '这里输入用户名（仅英文/空格，最多16位）' },
  username_invalid: { en: 'Username must be 1–16 letters/spaces, no symbols.', zh: '用户名仅支持英文和空格，长度1-16，不支持任何符号。' },
  username_limit_reached: { en: 'You can change username up to 3 times per year.', zh: '用户名每年最多修改3次。' },
  high_fidelity_engine: { en: 'High-Fidelity Physics Engine • Lab v1.0', zh: 'NEMOLIGHTLAB版本号：2025-V 1.0' },
  supported_formats: { en: 'Supported formats: PNG, JPG, WebP', zh: '支持图片格式: PNG, JPG, WebP' },
  back_to_site: { en: 'Back to site', zh: '返回官网' },
  generate: { en: 'Generate', zh: '生成' },
  angle_transform: { en: 'Light Reconstruction', zh: '光影重构' },
  text_reconstruct: { en: 'Text Reconstruction', zh: '文本重构' },
  physical_reconstruct: { en: 'Physical Reconstruction', zh: '物理重构' },
  upscale: { en: 'Upscale', zh: '高清放大' },
  tone_mapping: { en: 'Tone Mapping', zh: '色调映射' },
  photopea: { en: 'Photopea', zh: 'Photopea' },
  photopea_open_hint: {
    en: 'Open Photopea with the current rendered image (public URL / upload to COS)',
    zh: '用当前渲染图打开 Photopea（公网链接或上传至 COS 后打开）',
  },
  depth: { en: 'Depth', zh: '深度' },
  depth_normal_panel: { en: 'Depth & Normal', zh: '深度与法线' },
  depth_map_label: { en: 'Depth map', zh: '深度图' },
  normal_map_label: { en: 'Normal map', zh: '法线图' },
  depth_load_source: { en: 'Load image', zh: '载入图片' },
  depth_use_project_image: { en: 'Use project image', zh: '使用当前项目图' },
  depth_generate: { en: 'Generate', zh: '生成' },
  depth_runninghub_hint: {
    en: 'Requires RunningHub workflow with two image outputs (configure RUNNINGHUB_DEPTH_* on server).',
    zh: '需在后端配置 RunningHub 深度工作流（双图输出），环境变量见 RUNNINGHUB_DEPTH_*。',
  },
  tone_mapping_close: { en: 'Close', zh: '关闭' },
  tone_mapping_empty_hint: {
    en: 'Use “Add reference group” to load a reference image, extract a palette, and save it for the main workspace.',
    zh: '点击标题栏「添加参考组」，载入参考图、生成色板与 DNA，保存后可在主界面使用已存色板。',
  },
  tone_mapping_add_group: {
    en: 'Add reference group',
    zh: '添加参考组',
  },
  tone_mapping_add_hint: { en: 'Load images (max 3)', zh: '载入图片（最多3张）' },
  tone_mapping_image_error: { en: 'Cannot display', zh: '无法显示' },
  tone_mapping_delete_row: { en: 'Remove this group', zh: '删除本组' },
  tone_mapping_delete_confirm: { en: 'Delete this group of images?', zh: '确定删除本组图片吗？' },
  tone_mapping_yes: { en: 'YES', zh: '是' },
  tone_mapping_no: { en: 'NO', zh: '否' },
  tone_mapping_load_image: { en: 'Load image', zh: '载入图片' },
  /** 侧边栏：光影参考图 / 主图（ToneMappingModal SidebarImageSlot） */
  tone_mapping_load_shadow: { en: 'Load shadow', zh: '加载光影' },
  tone_mapping_load_main: { en: 'Load main', zh: '加载主图' },
  tone_mapping_generating: { en: 'Generating…', zh: '生成中…' },
  tone_mapping_palette_wait: { en: 'Wait for the palette to finish sampling.', zh: '请等待左侧色板采样完成后再生成。' },
  tone_mapping_compare_before: { en: 'Original', zh: '原图' },
  tone_mapping_compare_after: { en: 'Tone-mapped', zh: '映射结果' },
  tone_mapping_compare_hint: { en: 'Drag the slider · Scroll to zoom · Drag to pan when zoomed', zh: '拖动滑杆对比 · 滚轮缩放 · 放大后可拖拽平移' },
  tone_mapping_open_compare: { en: 'Fullscreen compare', zh: '全屏对比' },
  tone_mapping_dna_label: { en: 'Palette DNA', zh: '色板 DNA' },
  tone_mapping_dna_loading: { en: 'Analyzing tone…', zh: '正在分析影调…' },
  tone_mapping_preset_name_placeholder: {
    en: 'Name e.g. Minimal Italian',
    zh: '为色板起名，如：极简意式',
  },
  tone_mapping_save_preset: { en: 'Save palette', zh: '保存色板' },
  tone_mapping_save_login: { en: 'Log in to save palettes.', zh: '请先登录后再保存色板。' },
  tone_mapping_save_ok: { en: 'Palette saved.', zh: '色板已保存。' },
  tone_mapping_save_fail: { en: 'Save failed', zh: '保存失败' },
  sidebar_use_saved_palette: {
    en: 'Apply saved palette to render',
    zh: '使用已存色板参与生成',
  },
  sidebar_select_palette: { en: 'Select palette', zh: '选择色板' },
  tone_mapping_pro_login: {
    en: 'Please sign in to use Nano Banana Pro full-scene re-render (same credits as main Generate).',
    zh: '请登录后使用 Nano Banana Pro 全图重渲染（消耗积分与主界面「生成」相同）。',
  },
  tone_mapping_pro_hint: {
    en: 'Pro re-render (Nano Banana Pro), not a local filter · same credits as Generate',
    zh: 'Pro 全图重渲染（Nano Banana Pro），非本地滤镜 · 积分与主界面「生成」相同',
  },
  project_archive: { en: 'Project Archive', zh: '项目存档' },
  user_account: { en: 'User Account', zh: '用户账户' },
  physics_based_multi: { en: 'Physics-based lighting. Multi-select available.', zh: '基于物理的灯光模拟。支持多选功能。' },
  feedback_email: { en: 'Suggestions & feedback: 2 working days reply', zh: '建议和意见及问题反馈邮箱:2个工作日回复' },
  feedback_email_dialog_title: { en: 'Feedback email', zh: '反馈邮箱' },
  feedback_email_copy: { en: 'Copy', zh: '复制' },
  feedback_email_copied: { en: 'Copied to clipboard.', zh: '已复制到剪贴板。' },
  feedback_email_open_client: { en: 'Open mail app', zh: '打开邮件客户端' },
  detected_fixtures: { en: 'Detected Fixtures', zh: '识别光源' },
  hold_ctrl_multi: { en: 'Hold Ctrl to Multi-Select', zh: '按住 Ctrl 进行多选，同时调节' },
  adjusted: { en: 'ADJUSTED', zh: '已调整' },
  photon_properties: { en: 'Photon Properties', zh: '光子属性' },
  batch_properties: { en: 'Batch Properties ({n})', zh: '批量属性 ({n})' },
  disable_realtime: { en: 'Disable real-time preview', zh: '关闭实时预览' },
  enable_realtime: { en: 'Enable real-time preview', zh: '开启实时预览' },
  realtime: { en: 'REALTIME', zh: '实时' },
  reset_all: { en: 'RESET ALL', zh: '全部重置' },
  intensity: { en: 'Intensity', zh: '强度' },
  color_temperature: { en: 'Color Temperature', zh: '色温' },
  color: { en: 'Color', zh: '颜色' },
  color_hsl_hint: { en: 'e.g. warmer / cooler / brighter / darker', zh: '如：暖一点 / 冷一点 / 亮一点 / 暗一点' },
  hue: { en: 'H', zh: '色相' },
  saturation: { en: 'S', zh: '饱和度' },
  lightness: { en: 'L', zh: '亮度' },
  add_light_source: { en: 'Add Light Source', zh: '增加光源' },
  edit_mode_click: { en: 'Edit Mode: Click on the image to add light source', zh: '编辑模式：点击图片添加光源' },
  selected_type: { en: 'Selected Type', zh: '已选择类型' },
  press_esc_exit: { en: 'Press ESC to exit edit mode', zh: '按 ESC 键退出编辑模式' },
  confirm: { en: 'CONFIRM', zh: '确定' },
  upload_image: { en: 'Upload Image', zh: '上传图片' },
  click_load_upload: { en: 'Click "Load Project" or "Click Load Image" to upload (supports image resolution less than 2048×2048).', zh: '点击"加载项目"或"点击加载图片"上传图片（支持图片分辨率小于2048×2048）。' },
  auto_detect_lights: {
    en: 'Click the Recognize Light button to detect lights in the image.',
    zh: '点击识别光源按钮识别图片中的光源',
  },
  system_auto_detects: {
    en: 'After loading an image, use the Recognize button; upload alone does not run detection.',
    zh: '',
  },
  select_adjust: { en: 'Select & adjust (right panel)', zh: '选择与调节（右侧调节面板）' },
  click_select_ctrl: { en: 'Click recognized light points on the image (selected lights show a green breathing highlight); hold Ctrl for multi-select; adjust intensity, color temperature, and color.', zh: '点击选择图片上已经识别的光源光点（被选中的光源光点为绿色呼吸状态显示）,按住Ctrl可多选,调节强度、色温和颜色' },
  click_reset_all: { en: 'Click "Reset All" in the right light layer to restore initial state.', zh: '点击右侧灯光图层"全部重置"回到初始识别灯光状态。' },
  drag_sliders: { en: 'Drag sliders on image or use right panel.', zh: '图片上拖动滑杆可调节灯光强度、色温、颜色,右侧灯光调节面板可同样调节。' },
  recognized_layers: { en: 'Recognized lights show as layers; adjusted ones are marked.', zh: '被识别的灯光,会以图层的方式显示在右侧灯光图层面板。被调整过的灯光,图层会显示。' },
  in_light_layer_ctrl: { en: 'In light layer, hold Ctrl to multi-select and adjust.', zh: '在灯光图层,按住Ctrl键可多选灯光同时调节。' },
  generate_effect: { en: 'Generate effect', zh: '生成效果' },
  click_generate: { en: 'Click "Generate" to render enhanced lighting.', zh: '点击"生成"按钮渲染增强的灯光效果。' },
  hd_zoom: { en: 'HD Zoom', zh: '高清放大' },
  after_generation_hd: { en: 'After generation, use "HD Zoom" to improve resolution.', zh: '生成后可使用"高清放大"功能提升图片分辨率。' },
  unlock: { en: 'Unlock', zh: '解锁' },
  double_click_undo: { en: 'Double-click a recognition point to unlock/lock, Delete to remove a light, Ctrl+Z to undo. In the Light Layers panel, double-click the lock icon to lock/unlock the corresponding light on the image.', zh: '双击对灯光识别点解锁/上锁,Delete删除灯光,Ctrl+Z回退。同时在灯光图层面板可以双击锁头标志，对图片上的光源光点进行解锁上锁。' },
  tone_mapping_sidebar_guide: {
    en: 'Drag in the images to migrate from and the reference lighting, drag in the original image, then click Generate.',
    zh: '拖入要迁移和参考的光源图片，拖入原始图片，点击生成。',
  },
  /** 使用说明：光影映射（加载光影 / 主图） */
  sidebar_guide_light_shadow_mapping_title: {
    en: 'Light & shadow mapping',
    zh: '光影映射',
  },
  sidebar_guide_light_shadow_mapping_desc: {
    en: 'Under “Load shadow”, load a shadow or shadow-tone reference image; under “Load main”, load the main image to receive the transferred light and tone; then tap Generate image.',
    zh: '在加载光影位置载入光影图或者光影色调的图片，加载主图载入要被迁移光影和色调的主图，点击生图。',
  },
  /** 使用说明：文字重构 */
  sidebar_guide_text_reconstruct_title: {
    en: 'Text reconstruction',
    zh: '文字重构',
  },
  sidebar_guide_text_reconstruct_desc: {
    en: 'Load an image, enter a text command, and tap Generate.',
    zh: '载入图片，输入文字命令，点击生成。',
  },
  /** 使用说明：物理重构 */
  sidebar_guide_physical_reconstruct_desc: {
    en: 'Load an image, tap the button below the image to generate four images at once with different times of day and tones.',
    zh: '载入图片，点击图片下方按钮 一次生成4张不同时间不同色调的图片。',
  },
  /** 使用说明第 12 条：Photopea 外链编辑 */
  sidebar_guide_photopea_title: {
    en: 'Photopea',
    zh: 'Photopea',
  },
  sidebar_guide_photopea_desc: {
    en: 'After clicking Photopea, you are redirected to the official Photopea site; allow a few seconds for handoff. Works only on the image after generation. Capabilities are similar to Photoshop. This app does not charge for this link—it is only for your convenience. Any paid plans or top-ups are subject to Photopea’s official terms.',
    zh: '点击Photopea按钮后，自动跳转到Photopea官网，需要几秒钟的调度时间，只对生成后的图片生效，功能和PS几乎一样。本程序不收取费用，只为方便用户使用，具体充值与否请自行参考Photopea官网具体要求。',
  },
  sidebar_guide_chat_title: {
    en: 'Dialog below the image',
    zh: '图片下方对话框',
  },
  sidebar_guide_chat_desc: {
    en: 'After you run Generate once, you can use text commands to further refine the current lit result. Applies only to the lighting-generated result image.',
    zh: '在点击「生成」得到灯光渲染结果之后，可用文字命令继续微调当前结果图。只对灯光生成图生效。',
  },
  /** 使用说明第 6 条标题：强度与亮度（与序号同字号金色加粗） */
  sidebar_guide_intensity_hsl_title: {
    en: 'Intensity & brightness',
    zh: '强度和亮度',
  },
  /** 使用说明第 6 条正文：强度 vs 颜色区 HSL */
  sidebar_guide_intensity_hsl: {
    en: 'Too bright or too dark overall — adjust Intensity. Light color too pink/pale or too dull — adjust Saturation and Brightness (HSL under Color).',
    zh: '整体太亮或太暗请调节「强度」；光色太粉、太浅或太闷请调节「饱和度」「亮度」（颜色区 HSL）。',
  },
  /** 使用说明第 7 条：增加光源（灯位光子图） */
  sidebar_guide_add_light_title: {
    en: 'Add lights',
    zh: '增加光源',
  },
  sidebar_guide_add_light_desc: {
    en: 'On the light-position map after recognition, click where needed; adjust color temperature and color, then tap Generate. Note: AI recognition and generation are uncertain—if you are not satisfied, tap Generate again.',
    zh: '在识别后的灯位光子图，按照需要位置点击，调节色温及颜色后，点击生成，注：AI识别生成有不确定性，如不满意，可点击再次生成。',
  },
  hint_ai_vary: { en: 'Hint: AI detection may vary; you can click Recognize again.', zh: '提示：AI 识别结果每次可能略有不同；可再次点击「识别」图片。' },
  analyzing: { en: 'ANALYZING...', zh: '分析中...' },
  rendering: { en: 'RENDERING...', zh: '渲染中...' },
  image_analysis_wait: { en: 'Image analysis in progress, please wait...', zh: '图片正在分析中，请稍候...' },
  image_rendering_wait: { en: 'Image rendering in progress, please wait...', zh: '图片正在渲染中，请稍候...' },
  re_render_pro: { en: 'RE-RENDER PRO', zh: '重新生成' },
  click_to_load_image: { en: 'CLICK TO LOAD IMAGE', zh: '点击加载图片' },
  btn_recognize: { en: 'Recognize', zh: '识别光源' },
  /** 两次免费识别已用完：按钮显示为关闭态，引导先点「生成」 */
  btn_recognize_locked: {
    en: 'Recognize (locked — tap Generate)',
    zh: '识别已关闭 · 请先点「生成」',
  },
  recognize_quota_exhausted_hint: {
    en: 'Two free recognitions used. Click Generate (paid render) to continue, then you can recognize again — or upload a new image.',
    zh: '每张图仅 2 次免费「识别光源」。请先点击「生成」完成渲染（将扣费），之后可再次识别；或重新上传新图。',
  },
  recognize_need_image: { en: 'Please load an image first, then tap Recognize.', zh: '请先加载图片，再点击「识别」。' },
  recognize_again_prompt: {
    en: 'Recognize again (2nd attempt)? Current lights may be replaced. No third free attempt on this image.',
    zh: '再次识别（第 2 次）？可能会替换当前光源。同一张图没有第 3 次免费识别。',
  },
  error_gif_not_supported: {
    en: 'Animated GIF is not supported. Please use JPG, PNG, or WebP.',
    zh: '不支持 GIF 动图，请使用 JPG、PNG 或 WebP。',
  },
  btn_continue: { en: 'Continue', zh: '继续' },
  btn_cancel_short: { en: 'Cancel', zh: '取消' },
  high_resolution_ready: { en: 'Image resolution less than 2048×2048', zh: '图片分辨率小于2048×2048' },
  /** 底部命令输入框占位符（随语种） */
  text_command_placeholder: {
    en: 'Type a text command...',
    zh: '输入文字命令...',
  },
  command_send: { en: 'Send', zh: '发送' },
  command_add_images: { en: 'Add images', zh: '添加图片' },
  /** 文字命令：主画布图为「图1」，底部参考图从左到右为图2–图4 */
  command_slot_main_fig: { en: 'Fig.1', zh: '图1' },
  command_ref_fig_2: { en: 'Fig.2', zh: '图2' },
  command_ref_fig_3: { en: 'Fig.3', zh: '图3' },
  command_ref_fig_4: { en: 'Fig.4', zh: '图4' },
  command_max_reference_images_hint: {
    en: 'Up to 3 reference images (Fig.2–Fig.4). Drag files here or use +.',
    zh: '最多添加 3 张参考图（图2–图4），可拖到下方输入栏或使用 + 添加。',
  },
  command_reference_full_hint: {
    en: 'Maximum 3 reference images reached.',
    zh: '已达到 3 张参考图上限（图2–图4）。',
  },
  command_hint_sending: { en: 'Sending...', zh: '发送中...' },
  command_hint_sent: { en: 'Sent.', zh: '发送成功' },
  command_hint_failed: { en: 'Send failed.', zh: '发送失败' },
  command_render_complete: {
    en: 'Rendering complete.',
    zh: '渲染完成',
  },
  original: { en: 'Original', zh: '原图' },
  results: { en: 'Results', zh: '结果' },
  results_chart: { en: 'Results', zh: '结果图' },
  compare: { en: 'Compare', zh: '对比' },
  scanning_identification: { en: 'Deep lighting analysis in progress, please wait (estimated 1-2 minutes)...', zh: '正在进行深度光影分析，请稍候（预计需1-2分钟）…' },
  spatial_scanning: { en: 'Scanning spatial structure...', zh: '空间结构扫描中...' },
  spatial_sync_failed: { en: 'Spatial data sync failed. Please try again.', zh: '空间数据同步失败，请重试' },
  locked_double_click: { en: 'Locked (Double-click to unlock)', zh: '已锁定（双击解锁）' },
  unlocked_double_click: { en: 'Unlocked (Double-click to lock)', zh: '未锁定（双击锁定）' },
  // 灯具类型（检测到的灯具列表等随语种显示）
  fixture_sunlight: { en: 'Sunlight', zh: '阳光' },
  fixture_natural_light: { en: 'Natural Light', zh: '自然光' },
  fixture_ambient_light: { en: 'Ambient Light', zh: '环境光' },
  fixture_wall_lamp: { en: 'Wall Sconce', zh: '壁灯' },
  fixture_pendant: { en: 'Pendant', zh: '吊灯' },
  fixture_table_lamp: { en: 'Table Lamp', zh: '台灯' },
  fixture_floor_lamp: { en: 'Floor Lamp', zh: '落地灯' },
  fixture_spotlight: { en: 'Spotlight', zh: '射灯' },
  fixture_downlight: { en: 'Downlight', zh: '筒灯' },
  fixture_window: { en: 'Window', zh: '窗户' },
  fixture_candle: { en: 'Candle', zh: '蜡烛' },
  fixture_cove_light: { en: 'Cove Lighting', zh: '槽灯/暗光灯' },
  fixture_other: { en: 'Other', zh: '其他' },
  // 增加光源 16 个按钮（红框内，随语种显示）
  add_light_pendant: { en: 'Pendant', zh: '吊灯' },
  add_light_ceiling: { en: 'Ceiling Light', zh: '吸顶灯' },
  add_light_downlight: { en: 'Downlight', zh: '筒灯' },
  add_light_spotlight: { en: 'Spotlight', zh: '射灯' },
  add_light_track: { en: 'Track Light', zh: '轨道灯' },
  add_light_wall_sconce: { en: 'Wall Sconce', zh: '壁灯' },
  add_light_floor_lamp: { en: 'Floor Lamp', zh: '落地灯' },
  add_light_table_lamp: { en: 'Table Lamp', zh: '台灯' },
  add_light_led_strip: { en: 'LED Strip', zh: '灯带' },
  add_light_wall_washer: { en: 'Membrane Light', zh: '膜光' },
  add_light_membrane: { en: 'Membrane Light', zh: '膜光' },
  add_light_backlit: { en: 'Backlit', zh: '光带' },
  /** 手动「筒灯」入口下方说明 */
  add_light_downlight_scope_hint: {
    en: 'Note: applies only to objects or spaces with regular edges.',
    zh: '注：仅对边缘规则物体或空间生效',
  },
  add_light_luminous_ceiling: { en: 'Luminous Ceiling', zh: '发光顶棚' },
  add_light_window: { en: 'Window Light', zh: '自然窗光' },
  add_light_street: { en: 'Street Light', zh: '路灯' },
  add_light_neon: { en: 'Neon', zh: '霓虹灯' },
  add_light_car: { en: 'Car Headlights', zh: '车灯' },
  emission_mode: { en: 'Emission Mode', zh: '发光模式' },
  emission_up: { en: 'Up', zh: '上照' },
  emission_down: { en: 'Down', zh: '下照' },
  emission_up_down: { en: 'Up+Down', zh: '上下照' },
};

/** 根据当前语言取文案，缺省回退到英文 */
export function t(key: keyof typeof messages, lang: Lang): string {
  const msg = messages[key];
  if (!msg) return key;
  return msg[lang] ?? msg.en ?? key;
}

/** 带占位符的文案，如 batch_properties 的 {n} */
export function tReplace(key: keyof typeof messages, lang: Lang, replacements: Record<string, string>): string {
  let s = t(key, lang);
  Object.entries(replacements).forEach(([k, v]) => { s = s.replace(`{${k}}`, v); });
  return s;
}

/** 兼容旧用法：仅 en/zh */
export function tLegacy(en: string, zh: string, lang: Lang): string {
  if (lang === 'zh') return zh;
  return en;
}
